import { getCartPairingBundle } from '@hungryroot/cart';
import { Paragraph, Button } from '@hungryroot/ramen';
import {
  Divider,
  IngredientsList,
  Nutrition,
  Instructions,
  formatNutritionValue,
  nutritionDataToNutritionTable,
  stringNumberToNumberNoDecimal,
} from '@hungryroot/rdp';
import {
  type PairingDetail,
  type PairingV1 as Pairing,
} from '@hungryroot/rest';
import {
  type PairingWithIngredientDetails,
  type UseEditRecipeResult,
} from '../../../../hooks/useEditRecipe/useEditRecipe';
import { useEditRecipeMutations } from '../../../edit-recipe/hooks/useEditRecipeMutations/useEditRecipeMutations';
import { PairingEditIngredientEditorModal } from '../../../edit-recipe/PairingEdit/PairingEdit';
import { useRDPModalAnalytics } from '../hooks/useRDPModalAnalytics';
import styles from '../RDPModal.module.css';

interface RDPModalContentProps {
  pairingV2: PairingDetail | undefined;
  currentPairing: PairingWithIngredientDetails;
  editRecipeQuery: UseEditRecipeResult;
  onImageClick: (productId: number) => void;
  onNutritionToggle: (isExpanded: boolean) => void;
  isEdited: boolean;
  isLoadingAccountStatus: boolean;
  index: number | null;
  id: number;
  isAccountInActive: boolean;
  initialPairing: Pairing;
  onDismiss: () => void;
  modals: {
    isEditOpen: boolean;
    setIsEditOpen: (value: boolean) => void;
    isEditRecipeModalOpen: boolean;
    setIsEditRecipeModalOpen: (value: boolean) => void;
  };
}

export const RDPModalContent = ({
  pairingV2,
  currentPairing,
  editRecipeQuery,
  onImageClick,
  onNutritionToggle,
  isEdited,
  isLoadingAccountStatus,
  index,
  id,
  isAccountInActive,
  modals,
  initialPairing,
  onDismiss,
}: RDPModalContentProps) => {
  const nutrition = pairingV2?.nutrition;
  const { updateBundle } = useEditRecipeMutations();
  const analytics = useRDPModalAnalytics();
  const { servings, cooking_time, short_instruction_html } = currentPairing;

  const productInfo = [
    {
      label: 'Calories',
      value: `${
        nutrition
          ? stringNumberToNumberNoDecimal(nutrition.calories.toString())
          : ''
      }`,
    },
    {
      label: 'Fat',
      value: `${
        nutrition ? formatNutritionValue(nutrition.total_fat, 'total_fat') : ''
      }`,
    },
    {
      label: 'Carbs',
      value: `${
        nutrition
          ? formatNutritionValue(nutrition.total_carb, 'total_carb')
          : ''
      }`,
    },
    {
      label: 'Protein',
      value: `${
        nutrition ? formatNutritionValue(nutrition.protein, 'protein') : ''
      }`,
    },
    {
      label: 'Sugars',
      value: `${
        nutrition ? formatNutritionValue(nutrition.sugars, 'sugars') : ''
      }`,
    },
  ];

  return (
    <>
      <Divider />
      <IngredientsList
        onClick={(ingredient) => onImageClick(ingredient.id)}
        ingredients={
          currentPairing
            ? currentPairing.ingredients.map((ingredient) => {
                const isRemoved = !(
                  editRecipeQuery.bundle.order_details &&
                  editRecipeQuery.bundle.order_details.find((p) => {
                    return p.product === ingredient.id;
                  })
                );

                return {
                  id: ingredient.id,
                  discount: ingredient.discount,
                  name: ingredient.name,
                  imageUrl: ingredient.packaged_image_url,
                  subText: ingredient.brand_name,
                  isRemoved,
                  amount: ingredient.amount,
                };
              })
            : []
        }
      />
      <div className={styles.button}>
        {isLoadingAccountStatus === false && (
          <Button
            width="full-width"
            size="medium"
            style="default"
            prominence="secondary"
            onClick={() => {
              analytics.trackEditIngredients(id, index);
              isAccountInActive
                ? modals.setIsEditRecipeModalOpen(true)
                : modals.setIsEditOpen(true);
            }}
          >
            Edit Ingredients
          </Button>
        )}
      </div>
      <Divider />
      <Nutrition
        onShowMoreNutrition={onNutritionToggle}
        editWarning={
          isEdited
            ? 'Nutrition may not accurately reflect any edits made to the ingredients.'
            : ''
        }
        servings={
          <>
            <Paragraph className={styles['serving-size']}>
              Servings {servings}
            </Paragraph>
            <Paragraph className={styles['amount-per-serving']}>
              Amount per serving
            </Paragraph>
          </>
        }
        productInfo={productInfo}
        sections={nutrition ? nutritionDataToNutritionTable(nutrition) : []}
        disclaimer="The % Daily Value (DV) is based on a 2,000 calorie diet. Calorie amount includes all olive oil mentioned in the recipe."
      />
      <Divider />
      <Instructions
        disclaimer={
          isEdited
            ? 'Instructions may not accurately reflect any edits made to the ingredients.'
            : ''
        }
        instructions={short_instruction_html}
        cookTime={cooking_time}
      />
      {modals.isEditOpen && (
        <PairingEditIngredientEditorModal
          index={index}
          contentClassName={styles['recipe-editor']}
          testid="edit-recipe-modal"
          isOpen={modals.isEditOpen}
          onDismiss={() => {
            modals.setIsEditOpen(false);
          }}
          preventResetProducts={true}
          isModalEditor={true}
          isCustomModalEditor={true}
          onSubmitUpdate={async (params) => {
            const { modifications, cart, pairing: paramPairing } = params;

            try {
              const { id: bundleId } = getCartPairingBundle(
                cart,
                initialPairing.id
              );

              try {
                onDismiss();
                await updateBundle({
                  bundleId,
                  pairing: paramPairing,
                  modifications,
                });
              } catch {
                // if update bundle doesn't work, just dismiss modal
                onDismiss();
              }
            } catch {
              // if bundle id isn't found in cart, then it's a new pairing
              modals.setIsEditOpen(false);
            }
          }}
        />
      )}
    </>
  );
};
